<template>
  <v-row>
    <v-col cols="12" class="text-center">
      <h2 class="mt-6 mb-6">{{ t('Itinerario_formativo') }}
        <v-fab v-if="profesionalesDisponibles.length > 1" absolute location="top right" icon @click="openFiltratge">
          <v-icon>mdi-cog</v-icon>
        </v-fab>
      </h2>

      <h3 class="mt-6 mb-6" v-if="profesionalSeleccionado">{{ profesionalSeleccionado.categoria }}</h3>

      <!-- Contenedor principal para los años de la especialidad (R1, R2, R3...) -->
      <v-row v-if="!loading && !loadingprof && groupedCursosAnyo.length > 0">
        <!-- Recorremos todos los años desde el mínimo hasta el máximo encontrados -->
        <v-col v-for="(grupo, index) in groupedCursosAnyo" :key="index" cols="12" class="mb-8">
          <v-row align="center" justify="center">
            <v-col>
              <template v-if="grupo.isPreResidencia">
                <!-- Sin título -->
              </template>
              <template v-else-if="!grupo.isPostResidencia">
                <!-- Etiqueta del año: R1, R2, R3, etc. -->
                <div class="text-center mb-4">
                  <p class="text-disabled text-h1">R{{ grupo.anno + 1 }}</p>
                  <!-- Mostramos el avatar si coincide con el año en el que está el usuario -->
                  <div v-if="grupo.anno === userYearActual" class="d-inline-block mt-2">
                    <v-avatar color="info">
                      <v-icon icon="mdi-account-circle"></v-icon>
                    </v-avatar>
                  </div>
                </div>
              </template>

              <!-- Si es “post-residencia”, ponemos el label deseado -->
              <template v-else>
                <div class="text-center mb-4">
                  <h2 class="text-h5">Post-residencia</h2>
                </div>
              </template>
            </v-col>
            <v-col cols="12" md="10"><!-- Cursos de este grupo -->
              <v-row align="center" justify="center">
                <v-col v-for="curso in grupo.cursos" :key="curso.id" cols="auto" class="pa-2 d-inline-flex">
                  <BadgeCurs :curso="curso" :petit="grupo.cursos.length > 6"></BadgeCurs>
                </v-col>
              </v-row>
            </v-col>
          </v-row>

          <!-- Divider al final de cada bloque, excepto el último -->
          <v-divider v-if="index < groupedCursosAnyo.length - 1" class="mt-6"></v-divider>
        </v-col>
      </v-row>

      <!-- Nota y botón para mostrar otro itinerario si no hay cursos -->
      <v-row v-if="
        profesionalSeleccionado &&
        profesionalesDisponibles.length > 1 &&
        groupedCursosAnyo.length === 0
      ">
        <v-col cols="12" class="text-center">
          <p>{{ t('Tu_especialidad_todavia_no_tiene_itinerario_formativo') }}</p>
          <v-btn class="mt-4" color="primary" @click="openFiltratge">
            {{ t('Ver_otras_especialidades') }}
          </v-btn>
        </v-col>
      </v-row>
    </v-col>
  </v-row>
  <!-- Diálogo para Seleccionar especialidades -->
  <v-dialog v-model="open" max-width="600px">
    <v-card>
      <v-toolbar flat>
        <v-toolbar-title>{{ t('Seleccionar') }} {{ t('especialidad') }}</v-toolbar-title>
        <v-btn color="primary" text @click="applyFilter">{{ t('Aplicar') }}</v-btn>
        <v-btn icon @click="open = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-card-text>
        <v-select return-object v-model="profesionalSeleccionado" :items="profesionalesDisponibles"
          item-title="categoria" item-value="id" :label="t('Especialidad')">
        </v-select>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>


<script setup>
import { ref, computed, watch } from 'vue'
import { useUserStore } from '@/stores/user';
import { useCursosStore } from '@/stores/cursos';
import { useI18n } from 'vue-i18n'
import BadgeCurs from './BadgeCurs.vue';

const userStore = useUserStore();
const cursosStore = useCursosStore();
const { t } = useI18n()

const loading = computed(() => cursosStore.loadingCursos);
const loadingprof = computed(() => userStore.loadingProfesionales);
const loadinguserDetails = computed(() => userStore.loadingUserDetails);

const userDetails = computed(() => userStore.userDetails);
const userProfesional = computed(() => userDetails.value?.profesional ?? null);

// Variable reactiva para la selección de profesionales
const profesionalSeleccionado = ref(null)

// Diálogo de selección
const open = ref(false)

// Especialidades con al menos un curso asociado
const profesionalesDisponibles = computed(() => {
  if (loadingprof.value) return [];
  return userStore.profesionales.filter(prof =>
    cursosStore.cursos.some(curso =>
      curso.profesionales?.includes(prof.id)
    )
  );
});

// Watcher para cargar y preseleccionar especialidad del usuario
watch(
  [userProfesional, loadingprof, profesionalesDisponibles, loadinguserDetails],
  ([nuevoValor, cargandoProf, disponibles, cargandoUser]) => {
    if (!cargandoUser && !cargandoProf && nuevoValor) {
      const profSeleccionado = disponibles.find(p => p.id === nuevoValor)
      if (profSeleccionado) {
        profesionalSeleccionado.value = profSeleccionado
      }
    }
  },
  { immediate: true }
)

const selectedProfesionalId = computed(() => {
  return profesionalSeleccionado.value ? profesionalSeleccionado.value.id : null;
});


// Filtrado de cursos para la especialidad seleccionada
const cursosItinerario = computed(() => {
  if (!selectedProfesionalId.value) return [];
  return cursosStore.cursos.filter(c =>
    c.profesionales?.includes(selectedProfesionalId.value)
  );
});

// Función para calcular la “experiencia media” (o valor para orden) de un curso
function getExp(curso) {
  const expMin = curso.experiencia_minima
  const expMax = curso.experiencia_maxima
  // Si ambos valores existen, usar la media
  if (expMin != null && expMax != null) {
    return (expMin + expMax) / 2
  }
  // Si solo existe uno, usar ese
  if (expMin != null) return expMin
  if (expMax != null) return expMax
  // Si no hay ninguno, asignamos un valor “8” => post-residencia
  return 8
}

// Ordenamos los cursos por experiencia
const sortedCursosItinerario = computed(() => {
  return cursosItinerario.value.slice().sort((a, b) => getExp(a) - getExp(b))
})

/**
 * userYearActual: en qué “R” (año) se encuentra el usuario actualmente.
 * 0 => R1, 1 => R2, etc. Si pasa de 5, se queda en 5.
 */
const userYearActual = computed(() => {
  if (!userDetails.value || !userDetails.value.fecha_inicio_especialidad) {
    return 0
  }
  const fechaInicio = new Date(userDetails.value.fecha_inicio_especialidad)
  const hoy = new Date()
  const diffMs = hoy - fechaInicio
  // 365.25 días para aproximar años (incluyendo bisiestos)
  const diffEnAnios = diffMs / (1000 * 60 * 60 * 24 * 365.25)
  const year = Math.floor(diffEnAnios)
  return Math.min(year, 5)
})

/* ------------------ agrupación en bloques -------------------- */
const groupedCursosAnyo = computed(() => {
  const gruposResidencia = { 1: [], 2: [], 3: [], 4: [], 5: [] }
  const preResidencia = []
  const postResidencia = []

  let minYear = Infinity
  let maxYear = -Infinity

  for (const curso of sortedCursosItinerario.value) {
    const exp = getExp(curso)

    // Experiencia exactamente 0 → bloque "pre‑residencia"
    if (exp === 0) {
      preResidencia.push(curso)
      continue
    }

    // 1‑5  → residencia normal
    if (exp > 0 && exp <= 5) {
      const bracket = Math.ceil(exp)
      gruposResidencia[bracket].push(curso)
      if (bracket < minYear) minYear = bracket
      if (bracket > maxYear) maxYear = bracket
      continue
    }

    // Todo lo demás → post‑residencia
    postResidencia.push(curso)
  }

  const resultado = []

  if (preResidencia.length) {
    resultado.push({
      anno: null,
      cursos: preResidencia,
      isPreResidencia: true,
      isPostResidencia: false,
    })
  }

  if (maxYear !== -Infinity) {
    for (let y = minYear; y <= maxYear; y++) {
      resultado.push({
        anno: y - 1, // 1 → indice 0, así R1 = 0
        cursos: gruposResidencia[y],
        isPreResidencia: false,
        isPostResidencia: false,
      })
    }
  }

  if (postResidencia.length) {
    resultado.push({
      anno: null,
      cursos: postResidencia,
      isPreResidencia: false,
      isPostResidencia: true,
    })
  }

  return resultado
})

function openFiltratge() {
  open.value = true
}

function applyFilter() {
  open.value = false
}
</script>